import type { ClientError } from '@tribeplatform/gql-client/lib'
import {
  Action,
  ActionStatus,
  MutationSubscribeArgs,
} from '@tribeplatform/gql-client/types'

import {
  useMutation,
  UseMutationOptions,
} from '../../lib/react-query/useMutation.js'
import { useQueryClient } from '../../lib/react-query/useQueryClient.js'
import { useTribeClient } from '../../useTribeClient.js'
import { getMemberKey } from '../../utils/keys/member.key.js'
import { getSubscriptionStateKey } from '../../utils/keys/notification.key.js'
import { getPostKey } from '../../utils/keys/post.key.js'
import {
  bumpFollowStateVersion,
  followStateUpdater,
} from '../../utils/post-follow.util.js'
import { invalidateSpaceQueries } from '../../utils/space.utils.js'
import { updatePostCaches } from '../cache/useCachedPost.js'
import { useSpaceUtil } from '../utils/useSpaceUtil.js'

import type { SubscriptionState } from './useSubscriptionState.js'

type SubscribeArgs = MutationSubscribeArgs & {
  action: 'subscribe' | 'unsubscribe'
  publisherType: 'space' | 'post' | 'member'
}

type Context = { previous?: SubscriptionState }

export const useUpdateSubscription = (options?: {
  useMutationOptions?: UseMutationOptions<
    Action,
    ClientError,
    SubscribeArgs,
    Context
  >
}) => {
  const { useMutationOptions } = options || {}
  const { client } = useTribeClient()
  const queryClient = useQueryClient()
  const spaceUtils = useSpaceUtil()

  return useMutation<Action, ClientError, SubscribeArgs, Context>(
    ({ action, publisherId, level }) =>
      action === 'subscribe'
        ? client.mutation({
            name: 'subscribe',
            args: { fields: 'all', variables: { publisherId, level } },
          })
        : client.mutation({
            name: 'unsubscribe',
            args: { fields: 'all', variables: { publisherId } },
          }),
    {
      onMutate: ({ action, publisherType, publisherId }) => {
        if (publisherType === 'post') {
          bumpFollowStateVersion(publisherId)
        }
        const key = getSubscriptionStateKey({ variables: { publisherId } })
        const previous = queryClient.getQueryData<SubscriptionState>(key)
        // Keep the entry for the session; it has no observers to hold it.
        queryClient
          .getQueryCache()
          .build(queryClient, { queryKey: key, cacheTime: Infinity })
        queryClient.setQueryData<SubscriptionState>(key, {
          subscribed: action === 'subscribe',
        })
        return { previous }
      },
      onError: (_error, { publisherId }, context) => {
        const key = getSubscriptionStateKey({ variables: { publisherId } })
        if (context?.previous) {
          queryClient.setQueryData(key, context.previous)
        } else {
          queryClient.removeQueries({ queryKey: key, exact: true })
        }
      },
      onSettled: (
        data,
        error,
        { action, publisherType, publisherId, level },
      ) => {
        switch (publisherType) {
          case 'space': {
            invalidateSpaceQueries({
              queryClient,
              spaceId: publisherId,
              spaceUtils,
            })
            return undefined
          }

          case 'post': {
            if (!error && data?.status === ActionStatus.succeeded) {
              // The server remembers an unfollow, so a later reply won't follow the post again.
              updatePostCaches(
                publisherId,
                followStateUpdater(
                  action === 'subscribe',
                  level,
                  action === 'unsubscribe',
                ),
                queryClient,
              )
            }
            const postKey = getPostKey({ variables: { id: publisherId } })
            queryClient.invalidateQueries(postKey)
            return undefined
          }

          case 'member': {
            const memberKey = getMemberKey({ variables: { id: publisherId } })
            queryClient.invalidateQueries(memberKey)
            return undefined
          }

          default: {
            const _exhaustiveCheck: never = publisherType
            return _exhaustiveCheck
          }
        }
      },
      ...useMutationOptions,
    },
  )
}
